import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { useState, useEffect } from 'react';
import { updateData } from '../../../services';
import Modal from '../../helpers/Modal';
import { projectAddressFromCompany } from '../../../utils/projects';
import { useAddressSourceCompany } from '../../../utils/useAddressSourceCompany';
import {
    COUNTRIES,
    applyCountryChange,
    getPostalCodeLabel,
    getRegionSelectOptions,
    getStateProvinceLabel,
    normalizeRegionCode,
    resolveCountryCode,
} from '../../../context/usStates';

/** Trim each segment and drop empties; join with ", " for a stable stored value. */
const normalizeCommaSeparatedEmails = (raw) =>
    raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .join(', ');

const addressToFormData = (address) => ({
    full_name: address?.full_name || '',
    email: address?.email || '',
    phone: address?.phone || '',
    street1: address?.street1 || '',
    street2: address?.street2 || '',
    city: address?.city || '',
    state: normalizeRegionCode(address?.state),
    zip: address?.zip || '',
    country: resolveCountryCode(address?.country, address?.state)
});

export const EditAddressForm = ({
    project,
    addressType = 'shipping',
    buttonLabel,
    autoOpen = false,
    hideButton = false,
    notice = null,
    onClose,
}) => {
    const { token, base_url, setProjects } = useAppContext();
    const { addressCompany, isLoadingOwningCompany } = useAddressSourceCompany(project);
    const [isModalOpen, setIsModalOpen] = useState(autoOpen);
    const companyAddress = addressCompany ? projectAddressFromCompany(addressCompany, addressType) : null;
    const canCopyFromCompany = Boolean(addressCompany) || isLoadingOwningCompany;

    useEffect(() => {
        if (autoOpen) {
            setIsModalOpen(true);
        }
    }, [autoOpen]);

    const closeModal = (open) => {
        setIsModalOpen(open);
        if (!open && typeof onClose === 'function') {
            onClose();
        }
    };

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: 'US'
    });

    // Initialize form data with address values when component mounts or project changes
    useEffect(() => {
        const address = addressType === 'shipping' ? project.shipping_address : project.billing_address;
        setFormData(addressToFormData(address));
    }, [project, addressType]);

    const saveAddressFields = async (fields, { closeAfterSave = true } = {}) => {
        const existingAddress =
            addressType === 'shipping' ? project.shipping_address : project.billing_address;

        const data = {
            [addressType === 'shipping' ? 'shipping_address' : 'billing_address']: {
                ...(existingAddress && typeof existingAddress === 'object' ? existingAddress : {}),
                full_name: fields.full_name || '',
                email: normalizeCommaSeparatedEmails(fields.email || ''),
                phone: fields.phone || '',
                street1: fields.street1 || '',
                street2: fields.street2 || '',
                city: fields.city || '',
                state: fields.state || '',
                zip: fields.zip || '',
                country: resolveCountryCode(fields.country, fields.state)
            }
        };

        const updated_project = await updateData(token, `${base_url}/apis/projects/${project.id}`, data);

        if (updated_project.project) {
            setProjects((prevProjects) =>
                prevProjects.map((p) => (p.id === project.id ? updated_project.project : p))
            );

            window.showAlert(
                200,
                `${addressType.charAt(0).toUpperCase() + addressType.slice(1)} address updated successfully!`
            );
        }

        if (closeAfterSave) {
            closeModal(false);
        }
    };

    const handleFillFromCompany = () => {
        if (!companyAddress) return;
        setFormData(addressToFormData(companyAddress));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            console.log(`Updating project ${addressType} address for project:`, project.id);
            await saveAddressFields(formData);
        } catch (error) {
            console.error('Error updating address:', error);

            if (error.status === 404) {
                window.showAlert(404, 'Address not found');
            } else if (error.status >= 400 && error.status < 500) {
                window.showAlert(error.status, 'Invalid address data. Please check your input.');
            } else {
                window.showAlert(500, 'Failed to update address. Please try again.');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => (
            name === 'country' ? applyCountryChange(prev, value) : { ...prev, [name]: value }
        ));
    };

    const addressHeading =
        addressType === 'billing' ? 'Edit Billing Address' : 'Edit Shipping Address';
    const fieldId = (name) => `${addressType}-${name}`;
    const selectedCountry = resolveCountryCode(formData.country, formData.state);
    const regionOptions = getRegionSelectOptions(selectedCountry, formData.state);
    const stateLabel = getStateProvinceLabel(selectedCountry);
    const postalLabel = getPostalCodeLabel(selectedCountry);

    const editAddressForm = (
        <div>
            <h2 className="modal-title modal-title--compact">{addressHeading}</h2>
            {notice && (
                <p className="banner banner--warning banner--sm">
                    {notice}
                </p>
            )}
            {canCopyFromCompany && (
                <div className="copy-company-address copy-company-address--modal">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleFillFromCompany}
                        disabled={!companyAddress || isLoadingOwningCompany}
                        title={addressCompany?.company_name ? `Copy address from ${addressCompany.company_name}` : undefined}
                    >
                        Copy from company address
                    </button>
                </div>
            )}
            <form onSubmit={handleSubmit} className="company-form-grid">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="full_name" className="form-label">
                            Full Name
                        </label>
                        <input
                            type="text"
                            id="full_name"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter full name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone" className="form-label">
                            Phone Number *
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="email" className="form-label">
                            Email{addressType === 'billing' ? ' *' : ''}
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            multiple
                            value={formData.email}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="e.g. person1@company.com, person2@company.com"
                            title="Separate multiple addresses with commas"
                            required={addressType === 'billing'}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="street1" className="form-label">
                            Street Address 1
                        </label>
                        <input
                            type="text"
                            id="street1"
                            name="street1"
                            value={formData.street1}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter primary street address"
                            required={addressType === 'shipping'}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="street2" className="form-label">
                            Street Address 2
                        </label>
                        <input
                            type="text"
                            id="street2"
                            name="street2"
                            value={formData.street2}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter secondary address (optional)"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor={fieldId('country')} className="form-label">
                            Country
                        </label>
                        <select
                            id={fieldId('country')}
                            name="country"
                            value={selectedCountry}
                            onChange={handleChange}
                            className="form-select"
                            required={addressType === 'shipping'}
                        >
                            {COUNTRIES.map((country) => (
                                <option key={country.code} value={country.code}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor={fieldId('city')} className="form-label">
                            City
                        </label>
                        <input
                            type="text"
                            id={fieldId('city')}
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="Enter city"
                            required={addressType === 'shipping'}
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor={fieldId('state')} className="form-label">
                            {stateLabel}
                        </label>
                        <select
                            id={fieldId('state')}
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            className="form-select"
                            required={addressType === 'shipping'}
                        >
                            <option value="">Select a {stateLabel.toLowerCase()}</option>
                            {regionOptions.map((region) => (
                                <option key={region.abbr} value={region.abbr}>
                                    {region.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor={fieldId('zip')} className="form-label">
                            {postalLabel}
                        </label>
                        <input
                            type="text"
                            id={fieldId('zip')}
                            name="zip"
                            value={formData.zip}
                            onChange={handleChange}
                            className="form-input"
                            placeholder={selectedCountry === 'CA' ? 'A1A 1A1' : 'Enter zip code'}
                            required={addressType === 'shipping'}
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={() => closeModal(false)}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                    >
                        Update Address
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div>
            {!hideButton && (
                <button className="edit-address-button btn btn-action" onClick={() => setIsModalOpen(true)}>
                    <FontAwesomeIcon icon={faPen} />
                    {buttonLabel && <span className="edit-address-button-label btn-label-gap">{buttonLabel}</span>}
                </button>
            )}
            {isModalOpen && (
                <Modal form={editAddressForm} setIsModalOpen={closeModal} isWide fitContent />
            )}
        </div>
    )
};
